import styled, { css } from "styled-components";
import Card from "common/Boxes/Card";
import { MOBILE_SEARCH } from "common/constants";
import Text from "common/Typography/Text";
import {
  MAX_SECURITY_SETTINGS_WIDTH,
  MAX_SETTINGS_WIDTH,
  SMALL_MOBILE_CHECKOUT_HEIGHT,
} from "features/settings/common/constants";
import Flex from "common/Boxes/Flex.tsx";
import { NEW_LOOK_PAGE_GUTTER_VAR } from "common/NewLook";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

export const ConfigurationCard = styled(Card)`
  width: 100%;
  max-width: ${MAX_SETTINGS_WIDTH}px;
  border-radius: 12px;
  border: 0.2px solid ${(p) => p.theme.colors.lightestBorderGrey};
  padding: 20px;
  position: relative;
  color: ${(p) => p.theme.colors.textColor};
  h3 {
    color: ${(p) => p.theme.colors.titleColor};
    margin-bottom: 6px;
    font-size: ${(p) => p.theme.typography.fontSizes.L}px;
  }
  @media (max-width: 500px) {
    padding: 16px 12px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      border-radius: ${p.theme.radii.card}px;
      border: ${p.theme.borders.panel};
      box-shadow: ${p.theme.shadows.panel};
      padding: 20px;
      h3 {
        margin-bottom: 4px;
        color: ${p.theme.colors.titleColor};
        font-size: 16px;
        line-height: 22px;
        font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
      }
      @media (max-width: 500px) {
        padding: 16px;
      }
    `}
`;

export const SecurityConfigurationCard = styled(ConfigurationCard)`
  max-width: ${MAX_SECURITY_SETTINGS_WIDTH}px;
  @media (max-width: ${MOBILE_SEARCH}) {
    margin-top: 8px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      max-width: none;
      @media (max-width: ${MOBILE_SEARCH}) {
        margin-top: 0;
      }
    `}
`;

export const ValueAndActionWrapper = styled.div`
  display: flex;
  min-height: 45px;
  height: auto;
  align-items: center;
  padding: 6px 0;
  border-bottom: ${(p) => p.theme.borders.lightest};
  &:last-child {
    border-bottom: none;
    margin-bottom: -10px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* A flush panel row: 56px, 20px insets, hairlines between rows. */
      min-height: 60px;
      padding: 10px 20px;
      column-gap: 16px;
      border-bottom: 1px solid ${getNewLookTokens(p.theme).hairline};
      &:last-child {
        margin-bottom: 0;
      }
      @media (max-width: 359.98px) {
        flex-wrap: wrap;
        row-gap: 4px;
        padding: 10px 16px;
        > :first-child {
          flex: 1 1 100%;
        }
      }
    `}
`;

export const FieldName = styled(Text)<{ $flex?: number }>`
  color: ${(p) => p.theme.colors.mediumText};
  flex: ${(p) => p.$flex || "1 1 100px"};

  @media (max-width: ${MOBILE_SEARCH}) {
    flex: 1 1 80px;
    font-size: ${(p) => p.theme.typography.fontSizes.S};
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      &&& {
        ${newLookType.label};
        color: ${getNewLookTokens(p.theme).secondary};
      }
      flex: ${p.$flex || "0 0 180px"};
      @media (max-width: ${MOBILE_SEARCH}) {
        flex: 0 0 104px;
      }
    `}
`;

export const ValueAndAction = styled.div<{ $flex?: number }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: ${(p) => p.$flex || "1 1 160px"};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      min-width: 0;
      column-gap: 12px;
      ${newLookType.body};
      color: ${getNewLookTokens(p.theme).text};
      > .ant-typography:not(a) {
        ${newLookType.body};
        min-width: 0;
        overflow-wrap: anywhere;
        color: ${getNewLookTokens(p.theme).text};
      }
      /* Row actions read like the panels' "See all" links. */
      a,
      .ant-typography-link {
        flex: none;
        ${newLookType.label};
      }
    `}
`;

export const SettingsPageWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  flex-direction: column;
  position: relative;
  row-gap: 16px;

  @media (max-width: 500px) {
    padding: 12px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding: 0;
      align-items: stretch;
      @media (max-width: 500px) {
        padding: 0;
      }
    `}
`;

export const InitialPrice = styled(Text)`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  text-decoration: line-through;
  margin-right: 8px;
`;

export const FormWithSummaryWrapper = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  width: 100%;
  z-index: 1;
  ${(p) =>
    p.$isMobile
      ? `
    height: 100%;
    flex-direction: column;
    padding: 24px 0 0 0;
    @media (max-height: ${SMALL_MOBILE_CHECKOUT_HEIGHT}) {
      padding-top: 0;
    }
  `
      : `
    min-height: 100%;
  `};
`;
export const FormWrapper = styled(Flex)<{
  $isSmallScreen: boolean;
}>`
  align-items: flex-start;
  justify-content: center;
  padding: ${(p) => (p.$isSmallScreen ? 20 : 32)}px;
  height: 100%;
  width: 100%;
  background: ${(p) => p.theme.colors.billingBgColor};
  flex: auto;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      background: transparent;
    `}
`;
export const FormInnerWrapper = styled.div`
  max-width: 600px;
`;
export const SummaryWrapper = styled.div<{
  $isSmallScreen?: boolean;
}>`
  flex: ${(p) => (p.$isSmallScreen ? "0 0 400px" : "0 0 462px")};
  min-height: 100%;
  overflow: hidden;
  background: ${(p) => p.theme.colors.paymentBgColor};
  position: relative;
  padding-top: 10px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* A floating summary card instead of a half-height side panel. */
      align-self: flex-start;
      min-height: 0;
      margin: 24px ${NEW_LOOK_PAGE_GUTTER_VAR} 24px 0;
      padding-top: 0;
      border: ${p.theme.borders.panel};
      box-shadow: ${p.theme.shadows.panel};
      border-radius: ${p.theme.radii.card}px;
      background: ${p.theme.colors.cardLightBgColor};
    `}
`;
export const SummaryInnerWrapper = styled.div<{ $isSmallScreen: boolean }>`
  padding: ${(p) => (p.$isSmallScreen ? 20 : 32)}px;
`;
